import { jsx as _jsx } from "react/jsx-runtime";
import * as React from "react";
import { useRender } from "@base-ui-components/react/use-render";
import { cva } from "class-variance-authority";
import { cn } from "./lib/utils.js";
/**
 * The filled variants are two layers behind the label: `::before` is the solid
 * fill and the drop shadow, `::after` is the lit top edge and the hover wash.
 *
 * Both are inset by -1px, NOT 0. `inset: 0` on an absolutely positioned
 * pseudo-element resolves against the PADDING box, so on these variants (which
 * all carry a 1px transparent border) it lands 1px inside the button while
 * `rounded-[inherit]` still hands it the border box's radius. Two curves of
 * equal radius on boxes offset by 1px do not nest: they stay parallel down the
 * straights and diverge at every corner, and the element's own 90%-opacity
 * background shows through that ring as a pale rim that looks like a second,
 * wrong border radius.
 *
 * -1px puts the layers back on the border box, where the inherited radius is
 * the correct one. Correcting the radius instead would mean hardcoding
 * `radius - 1`, which breaks the moment a size changes it: `icon` is
 * rounded-xl, the rest rounded-lg.
 */
const depthLayers = "ui:relative ui:isolate ui:before:content-[''] ui:before:absolute ui:before:-inset-px ui:before:-z-10 ui:before:rounded-[inherit] ui:before:shadow-[0_1px_2px_0_rgb(0_0_0/0.05)] ui:after:content-[''] ui:after:absolute ui:after:-inset-px ui:after:-z-10 ui:after:rounded-[inherit] ui:after:shadow-[inset_0_1px_0_0_rgb(255_255_255/0.15)] ui:hover:after:bg-white/10";
const buttonVariants = cva("ui:inline-flex ui:cursor-default ui:items-center ui:justify-center ui:gap-1.5 ui:rounded-lg ui:font-semibold ui:whitespace-nowrap ui:transition-colors ui:focus-visible:border-border-focus ui:focus-visible:shadow-[0_0_0_3px_var(--m-accent-2-surface)] ui:focus-visible:outline-hidden ui:disabled:pointer-events-none ui:disabled:cursor-not-allowed ui:disabled:opacity-50 ui:[&_svg]:pointer-events-none ui:[&_svg]:shrink-0", {
    variants: {
        variant: {
            default: `${depthLayers} ui:border ui:border-transparent ui:bg-[oklch(0.60_0.14_137.4/0.9)] ui:text-fg-on-accent ui:before:bg-accent ui:disabled:bg-fg/12 ui:disabled:text-fg/38 ui:disabled:before:bg-transparent ui:disabled:after:hidden`,
            secondary: `${depthLayers} ui:border ui:border-transparent ui:bg-[oklch(0.32_0.07_235.9/0.9)] ui:text-white ui:dark:text-fg-on-accent ui:before:bg-accent-2 ui:disabled:bg-fg/12 ui:disabled:text-fg/38 ui:disabled:before:bg-transparent ui:disabled:after:hidden`,
            destructive: `${depthLayers} ui:border ui:border-transparent ui:bg-[oklch(0.46_0.16_29.9/0.9)] ui:text-white ui:dark:text-fg-on-accent ui:before:bg-error ui:disabled:bg-fg/12 ui:disabled:text-fg/38 ui:disabled:before:bg-transparent ui:disabled:after:hidden`,
            outline: "ui:border-[1.5px] ui:border-border-strong ui:bg-transparent ui:text-accent-2 ui:transition-colors ui:hover:border-accent-2 ui:hover:bg-accent-2-surface",
            subtle: "ui:border ui:border-transparent ui:bg-surface-0 ui:text-fg ui:transition-colors ui:hover:bg-surface-0/90",
            ghost: "ui:border ui:border-transparent ui:bg-transparent ui:text-fg-muted ui:transition-colors ui:hover:bg-surface-0",
            link: "ui:cursor-pointer ui:border-none ui:bg-transparent ui:p-0 ui:font-medium ui:text-accent-2 ui:underline ui:decoration-current/40 ui:underline-offset-2 ui:transition-colors ui:hover:decoration-current",
        },
        size: {
            default: "ui:px-[calc(--spacing(3.5)-1px)] ui:py-[calc(--spacing(2.5)-1px)] ui:text-base/6 ui:sm:px-[calc(--spacing(3)-1px)] ui:sm:py-[calc(--spacing(1.5)-1px)] ui:sm:text-sm/6 ui:[&_svg]:size-4",
            sm: "ui:px-[calc(--spacing(3)-1px)] ui:py-[calc(--spacing(1.5)-1px)] ui:text-sm/6 ui:sm:px-[calc(--spacing(2.5)-1px)] ui:sm:py-[calc(--spacing(1)-1px)] ui:sm:text-[0.8125rem]/6 ui:[&_svg]:size-3.5",
            lg: "ui:px-[calc(--spacing(4)-1px)] ui:py-[calc(--spacing(3)-1px)] ui:text-base/6 ui:sm:px-[calc(--spacing(3.5)-1px)] ui:sm:py-[calc(--spacing(2.5)-1px)] ui:sm:text-[0.9375rem]/6 ui:[&_svg]:size-5",
            icon: "ui:size-9 ui:rounded-xl ui:[&_svg]:size-4",
            "icon-xs": "ui:size-5 ui:rounded-md ui:[&_svg]:size-3.5",
            "icon-sm": "ui:size-7 ui:rounded-xl ui:[&_svg]:size-3.5",
            "icon-lg": "ui:size-11 ui:rounded-xl ui:[&_svg]:size-5",
        },
    },
    compoundVariants: [
        {
            variant: "ghost",
            size: ["icon", "icon-xs", "icon-sm", "icon-lg"],
            className: "ui:border ui:border-transparent ui:bg-transparent ui:text-fg-muted ui:transition-colors ui:hover:bg-surface-0",
        },
        {
            variant: "outline",
            size: ["icon", "icon-xs", "icon-sm", "icon-lg"],
            className: "ui:border ui:border-border ui:bg-surface-2 ui:text-fg-muted ui:transition-colors ui:hover:bg-surface-0",
        },
    ],
    defaultVariants: {
        variant: "default",
        size: "default",
    },
});
const Button = React.forwardRef(({ className, variant, size, asChild = false, children, ...props }, ref) => {
    // With `asChild`, our children prop is the element we compose onto, so it
    // is never forwarded — the child keeps its own children.
    const child = asChild && React.isValidElement(children)
        ? children
        : null;
    return useRender({
        render: child ?? _jsx("button", {}),
        ref,
        props: {
            className: cn(buttonVariants({ variant, size, className })),
            ...props,
            ...(child ? null : { children }),
        },
    });
});
Button.displayName = "Button";
export { Button, buttonVariants };
